import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Motivazione, Prov, MOTIVI_SCONTO } from "@/components/RichiestaCampi";
import { TipologiaSelect, CatastoSelect, tipoCatasto } from "@/lib/catalogo";

// Bozza unica della scheda: le modifiche di tutte le parti restano in sospeso finché non si preme Salva.
const Bozza = createContext({ drafts: {}, setDraft: () => {} });
function useBozza(k, base) {
  const { drafts, setDraft } = useContext(Bozza);
  const v = k in drafts ? drafts[k] : base;
  return [v, (nv) => setDraft(k, nv, base)];
}

function ClassEdit({ im }) {
  const [v, setV] = useBozza("class", { tipologia: im.tipologia, categoria_catastale: im.categoria_catastale || null });
  const conIncarico = im.stato && im.stato !== "censito";
  return (
    <div className="grid sm:grid-cols-2 gap-2 pt-1" data-testid="immobile-class-edit">
      <TipologiaSelect value={v.tipologia} testid="imd-tipologia" onChange={(t) => t && setV({ ...v, tipologia: t })} />
      <div>
        <CatastoSelect value={v.categoria_catastale} testid="imd-catasto" required={conIncarico} onChange={(c) => setV({ ...v, categoria_catastale: c || null })} />
        {conIncarico && !v.categoria_catastale && <p data-testid="imd-catasto-mancante" className="mt-1 text-xs font-semibold text-red-600">Obbligatoria per pubblicare</p>}
      </div>
    </div>
  );
}
import { Send } from "lucide-react";
import api, { API, euro, apiError, fmtDate, pctIt } from "@/lib/api";
import { StatusBadge, Progress, StatoImmobileBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import CambiaAgente from "@/components/CambiaAgente";
import SollecitiFermo from "@/components/SollecitiFermo";
import ComunicazioniModal from "@/components/ComunicazioniModal";
import { ReassignModal } from "@/pages/Acquirenti";
import { PhotoManager, DocumentManager } from "@/components/FileManager";
import { ChecklistDocAzioni } from "@/components/ChecklistDocAzioni";
import { ArchivioAzioni } from "@/components/ArchivioAzioni";
import { ServizioFoto } from "@/components/ServizioFoto";
import { PortaliPanel } from "@/components/PortaliPanel";
import { RiportaNotizia } from "@/components/RiportaNotizia";
import { toast } from "sonner";
import { ArrowLeft, Lock, Unlock, CheckCircle2, Circle, Clock, ShieldCheck, Users, Phone, Mail, Rocket, Building2, Footprints, FileText, History, MessageSquare, UserCog, Plus, Trash2, Save, Award, Pencil, BadgePercent, FileSignature } from "lucide-react";
import { Modal, Field, Select, Row } from "@/pages/Notizie";
import { UnsavedBar, useUnsavedGuard, salvatoOk, richiediUscita } from "@/components/UnsavedBar";
import { UltimoSalvataggio } from "@/components/SalvataggioUI";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import DeleteEntityDialog from "@/components/DeleteEntityDialog";
import DocLightbox from "@/components/DocLightbox";
import { PraticaView } from "@/pages/Documenti";
import { AppuntamentiScheda } from "@/components/AppuntamentiScheda";
import UnitoBanner from "@/components/UnitoBanner";
import { LongTextarea, NoteView } from "@/components/LongText";
import ChecklistTappe from "@/components/ChecklistTappe";
import ValuArt from "@/components/ValuArt";
import { PrezzoPubblicazione } from "@/components/ValutazioneImmobilart";
import ProvvigioniTab from "@/components/ProvvigioniTab";
import { DatiBaseEdit, ProprietariEdit, datiBaseBody } from "@/components/ImmobileDatiBase";
import { Doc30Badge, dataIt } from "@/components/Doc30Badge";

const CONF_OPTS = [["da_verificare", "Da verificare"], ["conforme", "Conforme"], ["non_conforme", "Non conforme"], ["in_sanatoria", "In sanatoria"]];

function VenditoreDocs({ propId }) {
  const [files, setFiles] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/files?entity_type=proprietario&entity_id=${propId}&kind=documento`).then((r) => { if (active) setFiles(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [propId]);
  if (!files.length) return null;
  const LABELS = { documento_identita: "Documento d'identità", codice_fiscale: "Codice Fiscale" };
  const alert = (s) => {
    if (!s) return null;
    const d = Math.ceil((new Date(s) - new Date()) / 86400000);
    let cls, label;
    if (d < 0) { cls = "text-red-600 bg-red-100 border-red-300"; label = "Scaduto"; }
    else if (d <= 7) { cls = "text-red-600 bg-red-100 border-red-300"; label = "Urgente"; }
    else if (d <= 15) { cls = "text-orange-600 bg-orange-100 border-orange-300"; label = "A breve"; }
    else if (d <= 30) { cls = "text-[#9A6A00] bg-[#FFB020]/15 border-[#FFB020]/40"; label = "Da pianificare"; }
    else if (d <= 60) { cls = "text-slate-500 bg-slate-100 border-slate-300"; label = "In programma"; }
    else return null;
    return <span className={`ml-2 inline-flex items-center text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${cls}`}>{label}{d >= 0 ? ` · ${d}gg` : ""}</span>;
  };
  return (
    <div className="pt-2 border-t border-[var(--border)]">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Documenti venditore</div>
      <div className="flex flex-col gap-1">
        {files.map((f) => (
          <button key={f.id} data-testid={`imm-venditore-doc-${f.id}`} onClick={() => setView(f)} className="flex items-center gap-2 text-xs text-slate-600 hover:text-[#FC82ED] text-left">
            <FileText size={13} className="text-[#FC82ED] shrink-0"/> {LABELS[f.doc_tipo] || "Documento"}: {f.original_filename}{alert(f.scadenza)}
          </button>
        ))}
      </div>
      {view && <DocLightbox file={view} onClose={() => setView(null)} />}
    </div>
  );
}

function IncaricoInline({ im }) {
  const [f, setF] = useBozza("incarico", { tipo_incarico: im.tipo_incarico || "", percentuale_provvigione: im.percentuale_provvigione ?? "" });
  const n = Number(f.percentuale_provvigione);
  const punti = f.tipo_incarico === "esclusiva" ? (n === 3 ? 2 : n === 4 ? 4 : 0) : 0;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="incarico-inline">
      <Select label="Tipo incarico" testid="incarico-tipo" value={f.tipo_incarico} onChange={(e) => setF({ ...f, tipo_incarico: e.target.value })} opts={[["", "— Seleziona —"], ["esclusiva", "Esclusiva"], ["non_esclusiva", "Non esclusiva"]]} />
      <Field label="% provvigione" testid="incarico-percentuale" type="number" value={f.percentuale_provvigione} onChange={(e) => setF({ ...f, percentuale_provvigione: e.target.value === "" ? "" : Number(e.target.value) })} />
      <div className="col-span-2 text-[11px] text-[var(--muted)]" data-testid="incarico-punti-preview">Punti contest con questi dati: <b className="text-amber-600">{punti} pt</b></div>
    </div>
  );
}

function CatastoImmobileTab({ im, readOnly }) {
  const initial = (Array.isArray(im.catasto_unita) && im.catasto_unita.length)
    ? im.catasto_unita
    : (im.catasto && Object.keys(im.catasto || {}).length ? [im.catasto] : [{}]);
  const [units, setUnits] = useBozza("catasto", initial);
  const setU = (i, k, v) => setUnits(units.map((u, idx) => idx === i ? { ...u, [k]: v } : u));
  const add = () => setUnits([...units, {}]);
  const rm = (i) => setUnits(units.filter((_, idx) => idx !== i));
  return (
    <div data-testid="immobile-catasto-tab" className="card-immo p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#FC82ED]">Unità catastali</h3>
        {!readOnly && <button data-testid="imm-catasto-add" onClick={add} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FC82ED] hover:underline"><Plus size={14} /> Aggiungi unità</button>}
      </div>
      <fieldset disabled={readOnly} className="space-y-3">
        {units.map((u, i) => (
          <div key={i} data-testid={`imm-catasto-unit-${i}`} className="rounded-xl border border-[var(--border)] p-4 bg-white/60">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)]">Unità {i + 1}</span>
              {!readOnly && units.length > 1 && <button data-testid={`imm-catasto-remove-${i}`} onClick={() => rm(i)} className="inline-flex items-center gap-1 text-[11px] font-medium text-red-500 hover:underline"><Trash2 size={13} /> Rimuovi</button>}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Foglio" testid={`imm-catasto-${i}-foglio`} value={u.foglio ?? ""} onChange={(e) => setU(i, "foglio", e.target.value)} />
              <Field label="Particella / Mappale" testid={`imm-catasto-${i}-particella`} value={u.particella ?? ""} onChange={(e) => setU(i, "particella", e.target.value)} />
              <Field label="Subalterno" testid={`imm-catasto-${i}-subalterno`} value={u.subalterno ?? ""} onChange={(e) => setU(i, "subalterno", e.target.value)} />
              <Field label="Categoria" testid={`imm-catasto-${i}-categoria`} value={u.categoria ?? ""} onChange={(e) => setU(i, "categoria", e.target.value)} />
              <Field label="Classe" testid={`imm-catasto-${i}-classe`} value={u.classe ?? ""} onChange={(e) => setU(i, "classe", e.target.value)} />
              <Field label="Consistenza" testid={`imm-catasto-${i}-consistenza`} value={u.consistenza ?? ""} onChange={(e) => setU(i, "consistenza", e.target.value)} />
              <Field label="Rendita catastale €" testid={`imm-catasto-${i}-rendita`} type="euro" value={u.rendita ?? ""} onChange={(e) => setU(i, "rendita", Number(e.target.value))} />
              <Field label="Superficie catastale" testid={`imm-catasto-${i}-superficie`} value={u.superficie ?? ""} onChange={(e) => setU(i, "superficie", e.target.value)} />
              <Field label="Intestatari catastali" testid={`imm-catasto-${i}-intestatari`} value={u.intestatari ?? ""} onChange={(e) => setU(i, "intestatari", e.target.value)} />
              <Select label="Conformità catastale" testid={`imm-catasto-${i}-conformita`} value={u.conformita ?? "da_verificare"} opts={CONF_OPTS} onChange={(e) => setU(i, "conformita", e.target.value)} />
            </div>
          </div>
        ))}
      </fieldset>
    </div>
  );
}

function OpenHouseModal({ id, im, matches, onClose }) {
  const [script, setScript] = useState("");
  const [esiti, setEsiti] = useState({});
  useEffect(() => { api.get("/settings/fase4").then((r) => setScript(r.data?.script_acquirente_openhouse || "")).catch(() => {}); }, []);
  const esito = async (acqId, val) => {
    try {
      const r = await api.post(`/open-house/${id}/esito`, { esito: val, acquirente_id: acqId });
      setEsiti((e) => ({ ...e, [acqId]: val }));
      if (val === "viene" && r.data?.appuntamento) toast.success(`Appuntamento open house creato (${fmtDate(r.data.data)}) nell'agenda dell'agente.`);
      else toast.success("Esito registrato");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="open-house-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-immo p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-1 font-display text-lg font-extrabold"><Footprints size={18} className="text-[#FC82ED]" /> Open house — acquirenti da chiamare</div>
        <p className="text-sm text-[var(--muted)] mb-3">{im.titolo || im.indirizzo}. Chiama gli acquirenti compatibili: con esito <b>"Viene"</b> parte un appuntamento automatico nell'agenda dell'agente.</p>
        {script && <div className="mb-3 p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-xs text-slate-600 italic">"{script}"</div>}
        {(!matches || matches.length === 0) ? <p className="text-sm text-[var(--muted)]">Nessun acquirente compatibile al momento.</p> :
          <div className="space-y-2">
            {matches.map((m) => (
              <div key={m.acquirente_id} data-testid={`oh-acq-${m.acquirente_id}`} className="p-3 rounded-xl border border-[var(--border)]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold">{m._masked ? m.nome_display : `${m.nome || ""} ${m.cognome || ""}`.trim()}</div>
                    <div className="text-xs text-[var(--muted)]">{m._masked ? `Agente: ${m.agente_referente}` : (m.telefono || "—")} · match {m.match}%</div>
                  </div>
                  {esiti[m.acquirente_id] && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">{esiti[m.acquirente_id] === "viene" ? "Viene" : esiti[m.acquirente_id]}</span>}
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {[["viene", "Viene"], ["non_viene", "Non viene"], ["richiama", "Richiama"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`oh-esito-${m.acquirente_id}-${v}`} onClick={() => esito(m.acquirente_id, v)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${v === "viene" ? "bg-[#FC82ED] text-black border-transparent" : "bg-[#F7F7F8] border-[var(--border)]"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>}
        <button onClick={onClose} className="mt-4 w-full py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Chiudi</button>
      </div>
    </div>
  );
}



const DOC_LABELS = {
  incarico: "Incarico firmato",
  privacy: "Privacy (firmata)",
  atto: "Atto di provenienza",
  ape: "APE",
  documento_identita: "Documento d'identità venditori",
  codice_fiscale: "Codice fiscale venditori",
  planimetria: "Planimetria catastale",
  visura: "Visura catastale",
  visura_ipotecaria: "Visura ipotecaria",
  delega: "Delega",
  condominiali: "Spese condominiali",
  are: "ARE",
  titoli_edilizi: "Documenti urbanistici",
  descrizione: "Descrizione (Word o PDF)",
  scheda_tecnica: "Scheda tecnica",
  altri_allegati: "Altri allegati",
};
const DOC_OPTIONAL = new Set(["altri_allegati", "descrizione", "scheda_tecnica", "titoli_edilizi"]);
const BLOCCHI = [
  { key: "pubblicare", titolo: "Obbligatori per pubblicare", keys: ["incarico", "privacy", "atto", "ape", "documento_identita", "codice_fiscale", "planimetria", "visura", "visura_ipotecaria"] },
  { key: "30giorni", titolo: "Entro 30 giorni dalla pubblicazione", keys: ["delega", "condominiali", "are"] },
  { key: "facoltativi", titolo: "Facoltativi", keys: ["titoli_edilizi", "descrizione", "scheda_tecnica", "altri_allegati"] },
];
const TABS = ["Panoramica", "Documenti", "Portali", "Catasto", "Matching", "Visite", "Proposte", "Provvigioni", "Appuntamenti", "Storico"];

export default function ImmobileDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isDirezione, isSegreteria, isDirettore } = useAuth();
  const [dubbio, setDubbio] = useState(null);
  const [decidi, setDecidi] = useState(null);
  const [notaD, setNotaD] = useState("");
  const [im, setIm] = useState(null);
  const [rejectDoc, setRejectDoc] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "documenti" ? "Documenti" : "Panoramica"));
  const [matches, setMatches] = useState([]);
  const [comm, setComm] = useState(false);
  const [reassign, setReassign] = useState(false);
  const [agents, setAgents] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [scontoModal, setScontoModal] = useState(false);
  const [rapporto, setRapporto] = useState(false);
  const [openHouse, setOpenHouse] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [bozzaSaving, setBozzaSaving] = useState(false);
  const [bozzaErr, setBozzaErr] = useState(null);
  const [provvErrs, setProvvErrs] = useState({});
  const [rev, setRev] = useState(0);
  const setDraft = (k, v, base) => setDrafts((p) => {
    const n = { ...p };
    if (base !== undefined && JSON.stringify(v) === JSON.stringify(base)) delete n[k]; else n[k] = v;
    return n;
  });
  const bozzaDirty = Object.keys(drafts).length > 0;
  const salvaTutto = async () => {
    const d = drafts;
    if (d.incarico && (!d.incarico.tipo_incarico || d.incarico.percentuale_provvigione === "" || Number(d.incarico.percentuale_provvigione) < 0)) {
      setBozzaErr("Dati incarico: scegli il tipo di incarico e una % provvigione valida."); return false;
    }
    setBozzaSaving(true); setBozzaErr(null); setProvvErrs({});
    const fatti = [];
    try {
      if (d.provv) {
        const r = await api.put(`/immobili/${id}/provvigioni`, d.provv);
        if (r.data?.in_attesa) toast.warning(`Provvigione sotto lo standard: inviata ${r.data.livello === "soci" ? "ai Soci" : "al Direttore"} in "Da approvare → Sconti provvigione".`);
        fatti.push("provv");
      }
      // Dopo le provvigioni: la % incarico scritta in Panoramica non viene sovrascritta dal vecchio valore della tab Provvigioni.
      const body = { ...(d.base ? datiBaseBody(d.base) : {}), ...(d.class || {}), ...(d.incarico || {}), ...(d.catasto ? { catasto_unita: d.catasto } : {}),
        ...(d.prop ? { proprietari: d.prop.ids, nuovi_proprietari: d.prop.nuovi } : {}),
        ...(d.valuart ? { valuart: d.valuart } : {}), ...(d.valut ? { valutazione: { ...(im.valutazione || {}), ...d.valut } } : {}) };
      if (Object.keys(body).length) {
        const r = await api.put(`/immobili/${id}`, body);
        if (r.data?.provvigione_in_attesa) toast.warning("% sotto lo standard: in attesa di approvazione in \"Da approvare → Sconti provvigione\".");
        if (r.data?.portali_da_aggiornare) toast.info("Dati aggiornati: verranno inviati ai portali al prossimo aggiornamento", { duration: 8000 });
        fatti.push("base", "class", "incarico", "catasto", "prop", "valuart", "valut");
      }
      if (d.omi) { await api.put(`/omi/scelta/immobile/${id}`, d.omi); fatti.push("omi"); }
      if (d.valuart) {
        try { await api.post(`/valuart/immobile/${id}/salva`, { input: d.valuart }); window.dispatchEvent(new CustomEvent("valuart-salvata", { detail: id })); } catch (e) { toast.warning(`Dati VALU-ART salvati, valutazione non registrata: ${apiError(e)}`); }
      }
      setDrafts({}); salvatoOk(); setRev((x) => x + 1); load();
      return true;
    } catch (e) {
      setDrafts((p) => { const n = { ...p }; fatti.forEach((k) => delete n[k]); return n; });
      const fe = e.response?.data?.detail?.field_errors; if (fe) setProvvErrs(fe);
      if (fatti.length) { setRev((x) => x + 1); load(); }
      setBozzaErr(`Salvataggio non riuscito: ${apiError(e)}`);
      return false;
    } finally { setBozzaSaving(false); }
  };
  const annullaTutto = () => { setDrafts({}); setBozzaErr(null); setProvvErrs({}); };
  const { dialog: uscitaDialog } = useUnsavedGuard(bozzaDirty, salvaTutto);
  const [rogitoBusy, setRogitoBusy] = useState(false);

  const canAzioni = () => im && (im.mine || isSegreteria || isDirezione);
  const faiRogito = async () => {
    if (!window.confirm("Confermi il rogito completato? Verranno creati i promemoria interni (recensione a 2 giorni, auguri a 1 anno) e la pratica sarà archiviata in Documenti.")) return;
    setRogitoBusy(true);
    try { await api.post(`/immobili/${id}/rogito`); toast.success("Rogito completato: promemoria creati e pratica archiviata."); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRogitoBusy(false); }
  };

  const load = () => api.get(`/immobili/${id}`).then((r) => setIm(r.data));
  useEffect(() => { load(); api.get(`/matching/immobile/${id}`).then((r) => setMatches(r.data)).catch(()=>{}); }, [id]);
  useEffect(() => { if (isDirezione) api.get("/agents?con_soci=true").then((r) => setAgents(r.data)); }, [isDirezione]);
  const [docFiles, setDocFiles] = useState([]);
  const loadDocFiles = () => api.get("/files", { params: { entity_type: "immobile", entity_id: id, kind: "documento" } }).then((r) => setDocFiles(r.data)).catch(() => {});
  useEffect(() => { if (tab === "Documenti") loadDocFiles(); }, [tab, id]); // eslint-disable-line

  if (!im) return <div className="skeleton h-96 rounded-2xl" />;

  const pct = Math.round((im.doc_completi / im.doc_totali) * 100);
  const canEditPresence = im.mine || isSegreteria;

  const toggleDoc = async (k) => {
    if (!canEditPresence) return;
    const next = !im.documenti_checklist?.[k];
    try { await api.put(`/immobili/${id}/checklist`, { [k]: next }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const approveDoc = async (k) => {
    try { await api.post(`/immobili/${id}/checklist/approva`, { doc: k }); load(); toast.success("Documento approvato."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitReject = async () => {
    if (!motivo.trim()) return toast.error("Inserisci una motivazione.");
    try {
      await api.post(`/immobili/${id}/checklist/rifiuta`, { doc: rejectDoc, motivo });
      setRejectDoc(null); setMotivo(""); load(); toast.success("Documento rifiutato.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitDubbio = async () => {
    if (!notaD.trim()) return toast.error("Scrivi una nota per il direttore.");
    try { await api.post(`/immobili/${id}/checklist/dubbio`, { doc: dubbio, nota: notaD }); setDubbio(null); setNotaD(""); load(); toast.success("Inviato al direttore."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitDecidi = async (azione) => {
    if (azione === "rimanda" && !notaD.trim()) return toast.error("Scrivi all'agente cosa correggere.");
    try { await api.post(`/immobili/${id}/checklist/dubbio/decidi`, { doc: decidi, azione, nota: notaD }); setDecidi(null); setNotaD(""); load(); toast.success(azione === "approva" ? "Documento approvato." : "Rimandato all'agente."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pubblica = async () => {
    try { await api.post(`/immobili/${id}/pubblica`); toast.success("Immobile pubblicato!"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Bozza.Provider value={{ drafts, setDraft }}>
    <div className="fade-up">
      <UnsavedBar dirty={bozzaDirty} saving={bozzaSaving} error={bozzaErr} onSave={salvaTutto} onCancel={annullaTutto} />
      {uscitaDialog}
      <UnitoBanner tipo="immobile" rec={im} onRestored={load} />
      <div className="flex items-center justify-end gap-3 mb-4">
        <div className="flex items-center gap-2">
          {(isSegreteria || isDirezione) && ["attivo", "in_trattativa", "proposta_ricevuta"].includes(im.stato) && (
            <button data-testid="rapporto-invia-ora-btn" onClick={() => setRapporto(true)}
                    className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#111111] px-3 py-1.5 rounded-lg hover:bg-[#F7F7F8] transition-colors">
              <Mail size={15} /> Rapporto proprietario
            </button>
          )}
          {im.mine && (
            <button data-testid="proponi-sconto-btn" onClick={() => setScontoModal(true)}
                    className="flex items-center gap-1.5 text-sm font-medium text-[#9A6A00] hover:text-[#111111] px-3 py-1.5 rounded-lg hover:bg-[#FFB020]/15 transition-colors">
              <BadgePercent size={15} /> Proponi sconto
            </button>
          )}
          {canAzioni() && im.stato !== "censito" && <ArchivioAzioni im={im} id={id} onDone={load} />}
          {canAzioni() && !["censito", "rogito_completato", "archiviato"].includes(im.stato) && (
            <button data-testid="open-house-btn" onClick={() => setOpenHouse(true)}
                    className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#111111] px-3 py-1.5 rounded-lg hover:bg-[#F7F7F8] transition-colors">
              <Footprints size={15} /> Open house
            </button>
          )}
          {canAzioni() && im.stato !== "rogito_completato" && im.stato !== "censito" && (
            <button data-testid="rogito-btn" onClick={faiRogito} disabled={rogitoBusy}
                    className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-50">
              <FileSignature size={15} /> {rogitoBusy ? "..." : "Rogito completato"}
            </button>
          )}
          {im.puo_modificare && <button data-testid="immobile-delete-btn" onClick={() => setShowDelete(true)}
                  className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
            <Trash2 size={15} /> Elimina immobile
          </button>}
        </div>
      </div>

      <div className="card-immo overflow-hidden mb-6">
        <div className="relative h-52 md:h-64 bg-white">
          {im.cover_file_id ? <img src={`${API}/files/${im.cover_file_id}/download`} alt="" data-testid="immobile-cover-hero" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><Building2 size={48}/></div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2 mb-2"><StatoImmobileBadge im={im} testid="immobile-stato" />{im.pubblicazione_bloccata && <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/90 text-[#111111] text-[10px] font-bold uppercase"><Lock size={11}/> Bloccato</span>}<Doc30Badge im={im} testid="immobile-doc30" /></div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#ffffff]">{im.titolo}</h1>
            <div className="text-sm text-slate-100">{im.codice} · {im.zona} · {im.comune}</div>
            <UltimoSalvataggio id={im.id} className="text-slate-200 mt-0.5" />
          </div>
        </div>
      </div>

      <TappeBarra tappa={im.tappa} stato={im.stato} />

      {(isDirezione || isDirettore) && !im._masked && <CambiaAgente im={im} onDone={load} />}
      <SollecitiFermo immobileId={id} />
      <RiportaNotizia im={{ ...im, id }} onDone={load} />

      {im.stato === "censito" ? (
        <CensitoPanel im={im} id={id} onDone={load} />
      ) : (<>
      {im.pubblicazione_bloccata && (
        <div className="card-immo p-5 mb-6 border-red-500/30 bg-red-500/[0.06] flex items-center gap-4 flex-wrap">
          <Lock className="text-red-400 shrink-0" size={24} />
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold text-red-400 uppercase text-sm">Pubblicazione Bloccata 🔒</div>
            <p className="text-sm text-slate-600 mt-0.5">Documenti obbligatori per pubblicare: {im.pub_completi ?? 0}/{im.pub_totali ?? 8} caricati e approvati dalla Segreteria.{(im.pub_mancanti || []).length > 0 && <> Manca: {im.pub_mancanti.join(", ")}.</>}</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="card-immo p-5"><div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">Prezzo</div><div className="mono font-extrabold text-2xl text-[#FC82ED]">{euro(im.prezzo)}</div></div>
        <div className="card-immo p-5"><div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">Superficie</div><div className="mono font-extrabold text-2xl">{im.mq || "-"} m²</div><div className="text-xs text-[var(--muted)]">{im.camere} camere · {im.bagni||0} bagni</div></div>
        <div className="card-immo p-5"><div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">Avanzamento Doc.</div><div className="mono font-extrabold text-2xl">{pctIt(pct)}</div><Progress value={pct} className="mt-2" /></div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-[var(--border)] overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
                  className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab===t ? "border-[#FC82ED] text-[#111111]" : "border-transparent text-[var(--muted)] hover:text-[#111111]"}`}>{t}</button>
        ))}
      </div>

      {tab === "Panoramica" && (
        <>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-immo p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><Users size={16}/> Proprietario</div>
              {!im._masked && <button data-testid="immobile-comm-btn" onClick={() => setComm(true)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#111111]"><MessageSquare size={13}/> Timeline</button>}
            </div>
            {im._masked ? <div className="text-sm text-[var(--muted)] flex items-center gap-2"><Lock size={14}/> Dati riservati · Agente {im.owner_agent_name}</div> :
            <div className="space-y-2 text-sm">
              <div className="font-medium">{im.proprietario_nome} {im.proprietario_cognome}</div>
              {im.proprietario_id && !im._masked && (
                <a data-testid="immobile-cliente-link" href={`/proprietari?open=${im.proprietario_id}`}
                   className="inline-flex items-center gap-1.5 text-xs font-medium text-[#FC82ED] hover:underline"><Users size={13}/> Apri scheda Cliente Venditore</a>
              )}
              {im.telefono && <div className="flex items-center gap-2 text-slate-500"><Phone size={13}/>{im.telefono}</div>}
              {im.email && <div className="flex items-center gap-2 text-slate-500"><Mail size={13}/>{im.email}</div>}
              {im.proprietario_id && !im._masked && <VenditoreDocs propId={im.proprietario_id} />}
              <ProprietariEdit key={`pe${rev}`} im={im} Bozza={Bozza} />
            </div>}
          </div>
          <div className="card-immo p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Dettagli</div>
              {isDirezione && <button data-testid="immobile-reassign-btn" onClick={() => setReassign(true)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#FC82ED]"><UserCog size={13}/> Riassegna</button>}
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <Kv k="Zona" v={im.zona} /><Kv k="Comune" v={im.comune} /><Kv k="Agente titolare" v={im.owner_agent_name} />
              {!im.puo_modificare ? <Kv k="Tipologia" v={tipoCatasto(im.tipologia, im.categoria_catastale)} /> : <ClassEdit im={im} />}
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--border)]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">Dati incarico</div>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                {(im.mine || isDirezione) ? <IncaricoInline im={im} /> : <>
                <Kv k="Tipo incarico" v={im.tipo_incarico === "esclusiva" ? "Esclusiva" : im.tipo_incarico === "non_esclusiva" ? "Non esclusiva" : "—"} />
                <Kv k="% provvigione" v={im.percentuale_provvigione != null ? pctIt(im.percentuale_provvigione) : "—"} /></>}
                <div className="flex justify-between"><span className="text-[var(--muted)]">Punti contest</span><span data-testid="incarico-punti" className="inline-flex items-center gap-1 font-bold text-amber-600"><Award size={13}/> {im.contest_punti_incarico || 0} pt</span></div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4"><DatiBaseEdit key={`db${rev}`} im={im} Bozza={Bozza} soci={isDirezione} /></div>
        {(() => {
          const units = (Array.isArray(im.catasto_unita) && im.catasto_unita.length) ? im.catasto_unita : (im.catasto && Object.keys(im.catasto || {}).length ? [im.catasto] : []);
          if (!units.length) return null;
          const ready = ["pronto_per_pubblicazione", "pronto", "attivo"].includes(im.stato);
          return (
            <div data-testid="catasto-summary" className={`card-immo p-5 mt-4 ${ready ? "border-[#FC82ED]/40 ring-1 ring-[#FC82ED]/20" : ""}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold flex items-center gap-2 text-sm">Riepilogo catasto · {units.length} {units.length === 1 ? "unità" : "unità"}{ready && <span className="text-[10px] uppercase font-bold text-[#FC82ED] bg-[#FC82ED]/10 border border-[#FC82ED]/30 px-1.5 py-0.5 rounded">Pronto pubblicazione</span>}</div>
                {im.puo_modificare && <button data-testid="catasto-summary-edit" onClick={() => setTab("Catasto")} className="text-xs font-medium text-[#FC82ED] hover:underline">Modifica →</button>}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {units.map((u, i) => (
                  <div key={i} data-testid={`catasto-summary-unit-${i}`} className="rounded-lg border border-[var(--border)] p-3 text-xs">
                    <div className="font-semibold text-[#111111] mb-1">Unità {i + 1}</div>
                    <div className="text-slate-600">Fg {u.foglio || "—"} · Part. {u.particella || "—"} · Sub {u.subalterno || "—"}</div>
                    <div className="text-slate-500">{u.categoria || "—"} · {(u.conformita || "da_verificare").replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        </>
      )}

      {tab === "Documenti" && (
        <div className="space-y-6">
          {(isDirezione || isSegreteria || isDirettore || im.mine) && <div className="max-w-2xl"><ServizioFoto im={im} onDone={load} /></div>}
          <div className="card-immo p-5 max-w-2xl">
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="font-semibold">Checklist Documentale
                {im.checklist_stato === "approvata" && <span className="ml-2 text-[11px] uppercase font-bold text-emerald-600 bg-emerald-400/10 border border-emerald-400/30 px-2 py-0.5 rounded-full">Checklist approvata</span>}
              </div>
              {isSegreteria && (
                <button data-testid="vai-portali-btn" onClick={() => setTab("Portali")} disabled={(im.pub_mancanti || [""]).length > 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none">
                  <Rocket size={15}/> Pubblica sui portali
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--muted)] mb-4">Caricati {im.doc_completi}/{im.doc_totali} · l'Agente carica i documenti, la <b className="text-[#111111]">Segreteria</b> li approva o rifiuta. Approvati tutti gli obbligatori per pubblicare, la Segreteria può pubblicarlo dalla scheda Portali; i documenti “entro 30 giorni” si consegnano dopo la pubblicazione.{im.checklist_approved_by && <> Approvata da <b>{im.checklist_approved_by}</b> il {fmtDate(im.checklist_approved_at)}.</>}</p>
            {isSegreteria && (im.pub_mancanti || []).length > 0 && (
              <div data-testid="pubblica-mancanti" className="mb-4 p-3 rounded-xl border border-red-200 bg-red-50 text-xs text-red-700">
                <b>Per pubblicare manca:</b> {im.pub_mancanti.join(", ")}
              </div>
            )}
            {BLOCCHI.map((b) => (
            <div key={b.key} data-testid={`blocco-${b.key}`} className="mb-5">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-xs font-bold uppercase tracking-wide text-[#111111]">{b.titolo}</div>
                {b.key === "pubblicare" && <span data-testid="contatore-pubblicare" className={`text-xs font-bold ${im.pub_completi === im.pub_totali ? "text-emerald-600" : "text-[#FFB020]"}`}>{im.pub_completi ?? 0}/{im.pub_totali ?? 8}</span>}
                {b.key === "30giorni" && <span data-testid="scadenza-30giorni" className={`text-xs font-semibold ${im.doc30_scaduti ? "text-red-600" : "text-[var(--muted)]"}`}>{im.doc30_scadenza ? `${im.doc30_scaduti ? "Scaduti il" : "Scadenza"} ${dataIt(im.doc30_scadenza)}` : "Parte alla pubblicazione"}</span>}
              </div>
              {b.key === "pubblicare" && (im.venditori_doc_mancanti || []).length > 0 && (
                <div data-testid="venditori-mancanti" className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">Documenti dei venditori (d'identità e codice fiscale di tutti i venditori collegati): {im.venditori_doc_mancanti.join("; ")}</div>
              )}
            <div className="space-y-2">
              {b.keys.map((k) => {
                const present = im.documenti_checklist?.[k];
                const verified = im.documenti_verificati?.[k];
                const info = im.documenti_verifica?.[k] || {};
                const rejected = info.stato === "rifiutato";
                const dubbioDoc = info.stato === "dubbio";
                const optional = DOC_OPTIONAL.has(k);
                const statusText = verified ? "Approvato dalla Segreteria" : rejected ? "Rifiutato · da correggere" : dubbioDoc ? "Dubbio · in attesa del direttore" : present ? "Caricato · in attesa di verifica" : "Da caricare";
                const statusColor = verified ? "text-emerald-500" : rejected ? "text-red-600" : dubbioDoc ? "text-violet-600" : present ? "text-[#FFB020]" : "text-[var(--muted)]";
                return (
                  <div key={k} data-testid={`doc-${k}`} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] flex-wrap sm:flex-nowrap">
                    <button data-testid={`doc-toggle-${k}`} onClick={() => toggleDoc(k)} disabled={!canEditPresence}
                            className="shrink-0 disabled:cursor-not-allowed" title={canEditPresence ? "Segna come caricato / non caricato" : "Solo l'agente titolare o la Segreteria"}>
                      {verified ? <ShieldCheck className="text-emerald-500" size={20} /> : rejected ? <Circle className="text-red-500" size={20} /> : present ? <Clock className="text-[#FFB020]" size={20} /> : <Circle className="text-[var(--muted)]" size={20} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {DOC_LABELS[k]}
                        {optional && <span className="text-[10px] uppercase font-bold text-[var(--muted)] bg-slate-100 border border-[var(--border)] px-1.5 py-0.5 rounded">Facoltativo</span>}
                      </div>
                      <div className={`text-[11px] mt-0.5 ${statusColor}`}>{statusText}</div>
                      {rejected && info.motivo && <NoteView testid={`reject-reason-${k}`} text={`Motivo: ${info.motivo}`} lines={3} className="text-[11px] mt-0.5 text-red-500" />}
                      {dubbioDoc && info.nota && <NoteView testid={`dubbio-nota-${k}`} text={`Nota segreteria: ${info.nota}`} lines={3} className="text-[11px] mt-0.5 text-violet-600" />}
                    </div>
                    <ChecklistDocAzioni entityType="immobile" entityId={id} tipo={k} label={DOC_LABELS[k]} files={docFiles} canUpload={canEditPresence || isDirezione} onDone={() => { load(); loadDocFiles(); }} />
                    {dubbioDoc && (isDirezione || isDirettore) ? (
                      <button data-testid={`dubbio-decidi-${k}`} onClick={() => { setDecidi(k); setNotaD(""); }}
                              className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100">Decidi</button>
                    ) : isSegreteria ? (
                      <div className="shrink-0 flex items-center gap-1.5">
                        <button data-testid={`approve-${k}`} onClick={() => approveDoc(k)} disabled={!present || verified}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-emerald-300 text-emerald-600 bg-emerald-50 hover:bg-emerald-100">
                          {verified ? <><CheckCircle2 size={13} /> Approvato</> : <><ShieldCheck size={13} /> Approva</>}
                        </button>
                        <button data-testid={`reject-${k}`} onClick={() => { setRejectDoc(k); setMotivo(""); }} disabled={!present}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-red-200 text-red-600 hover:bg-red-50">
                          Rifiuta
                        </button>
                        <button data-testid={`dubbio-${k}`} onClick={() => { setDubbio(k); setNotaD(""); }} disabled={!present || verified || dubbioDoc}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-violet-200 text-violet-700 hover:bg-violet-50">
                          Dubbio → al direttore
                        </button>
                      </div>
                    ) : (
                      <span data-testid={`verify-chip-${k}`} className={`shrink-0 text-[10px] uppercase font-bold px-2 py-1 rounded ${verified ? "text-emerald-600 bg-emerald-400/10 border border-emerald-400/30" : rejected ? "text-red-600 bg-red-500/10 border border-red-400/30" : present ? "text-[#FFB020] bg-[#FFB020]/10 border border-[#FFB020]/30" : "text-[var(--muted)] bg-slate-100 border border-[var(--border)]"}`}>
                        {verified ? "Approvato" : rejected ? "Da correggere" : dubbioDoc ? "Dal direttore" : present ? "In verifica" : "Mancante"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
            ))}
          </div>
          <div className="card-immo p-5">
            <DocumentManager entityType="immobile" entityId={id} readOnly={!im.mine && !isDirezione && !isSegreteria} />
          </div>
          <div className="card-immo p-5">
            <PhotoManager entityType="immobile" entityId={id} readOnly />
            {!im._masked && <button data-testid="gestisci-foto-marketing" onClick={() => nav(`/marketing/foto/${id}`)} className="mt-3 text-sm font-semibold text-[#B8369F] hover:underline">Gestisci foto in Reparto marketing →</button>}
          </div>
          {im.stato !== "censito" && (
            <div data-testid="immobile-pratica-cartelle">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2 text-[var(--muted)]">Archivio pratica (sezione Documenti)</div>
              <PraticaView immobileId={id} embedded />
            </div>
          )}
        </div>
      )}

      {tab === "Matching" && (
        <div className="space-y-3">
          {matches.length === 0 && <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">Nessun acquirente compatibile.</div>}
          {matches.map((m) => (
            <div key={m.acquirente_id} className="card-immo p-4 flex items-center gap-4 flex-wrap">
              <div className="relative w-14 h-14 shrink-0">
                <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90"><circle cx="18" cy="18" r="16" fill="none" stroke="#E5E7EB" strokeWidth="3"/><circle cx="18" cy="18" r="16" fill="none" stroke="#FC82ED" strokeWidth="3" strokeDasharray={`${m.match} 100`} strokeLinecap="round"/></svg>
                <span className="absolute inset-0 flex items-center justify-center mono font-bold text-xs">{m.match}%</span>
              </div>
              <div className="flex-1 min-w-[140px]">
                <div className="font-semibold">{m._masked ? m.nome_display : `${m.nome} ${m.cognome}`}</div>
                <div className="text-xs text-[var(--muted)]">Budget {euro(m.budget_min)}–{euro(m.budget_max)} · {m.zona}</div>
              </div>
              {m._masked ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[var(--muted)] flex items-center gap-1"><Lock size={12}/> Ref: {m.agente_referente}</span>
                  <button className="px-3 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium hover:border-[#FC82ED]/40" data-testid={`contact-agent-${m.acquirente_id}`} onClick={() => toast.info(`Richiesta inviata a ${m.agente_referente}`)}>Contatta agente referente</button>
                </div>
              ) : (
                <div className="text-xs text-slate-500 flex flex-col items-end">{m.telefono}<span>{m.email}</span></div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "Visite" && <ListSimple items={im.visite} icon={Footprints} render={(v) => `${v.acquirente_nome || "Acquirente"} · ${fmtDate(v.data)} · ${v.stato}`} empty="Nessuna visita registrata." />}
      {tab === "Proposte" && <ListSimple items={im.proposte} icon={FileText} render={(p) => `${p.acquirente_nome || "Acquirente"} · ${euro(p.importo)} · ${p.stato}`} empty="Nessuna proposta." />}
      {tab === "Provvigioni" && <ProvvigioniTab key={rev} id={id} draft={drafts.provv} onDraft={(v, base) => setDraft("provv", v, base)} errs={provvErrs}><RegolaOroCard im={im} id={id} onSaved={load} /></ProvvigioniTab>}
      {tab === "Storico" && <ListSimple items={(im.attivita || []).filter((a) => !(a.action === "update" && (im.attivita || []).some((b) => b.action === "modifica_campi" && String(b.timestamp).slice(0, 19) === String(a.timestamp).slice(0, 19))))} icon={History} render={(a) => (a.action === "modifica_campi" ? (
        <div data-testid="storico-modifica-campi"><div>{new Date(a.timestamp).toLocaleString("it-IT")} · <b>{a.user_name}</b> · modifica dati</div>
          {(a.new_value?.campi || []).map((c, j) => <div key={j} className="text-xs text-slate-600">{c.campo}: <span className="line-through text-slate-400">{String(c.prima ?? "—")}</span> → <b>{String(c.dopo ?? "—")}</b></div>)}</div>
      ) : `${fmtDate(a.timestamp)} · ${a.user_name} · ${a.action}`)} empty="Nessuna attività." />}
      {tab === "Catasto" && <CatastoImmobileTab im={im} readOnly={!im.puo_modificare} />}
      {tab === "Portali" && <PortaliPanel id={id} im={im} isSegreteria={isSegreteria} onChanged={(d) => setIm({ ...im, portali: d.portali, stato: d.stato })} />}
      {tab === "Appuntamenti" && (
        <div className="card-immo p-5">
          <AppuntamentiScheda entity={{ immobile_id: id }}
            prefill={{ property: { type: "immobile", id, label: im.titolo || im.indirizzo || "Immobile" },
              client: im.proprietario_id ? { type: "proprietario", id: im.proprietario_id, label: `${im.proprietario_nome || ""} ${im.proprietario_cognome || ""}`.trim() } : null,
              tipo: "visita", titolo: `Visita ${im.titolo || im.indirizzo || ""}`.trim() }} />
        </div>
      )}

      </>)}

      {comm && <ComunicazioniModal entityType="immobile" entityId={id} name={`${im.titolo} · ${im.proprietario_nome || ""} ${im.proprietario_cognome || ""}`} onClose={() => setComm(false)} />}
      {openHouse && <OpenHouseModal id={id} im={im} matches={matches} onClose={() => setOpenHouse(false)} />}
      {reassign && <ReassignModal entityType="immobile" record={{ id }} agents={agents} onClose={() => setReassign(false)} onSaved={() => { setReassign(false); load(); }} />}
      {im.pratica_bloccata && (
        <div data-testid="pratica-bloccata-banner" className="mx-0 mb-4 p-3 rounded-xl border-2 border-red-300 bg-red-50 text-sm text-red-700 font-semibold flex items-center gap-2">
          <Lock size={16} /> Pratica IN ROSSO — preliminare firmato senza provvigione incassata (Regola d'oro). Avvisati i Soci.
        </div>
      )}
      {tab !== "Provvigioni" && <RegolaOroCard im={im} id={id} onSaved={load} />}
      {im.stato !== "censito" && <ChecklistTappe id={id} />}
      {!im._masked && <div className="mb-6"><ValuArt key={`va-${rev}`} tipo="immobile" id={id} value={drafts.valuart || im.valuart} onChange={(v) => setDraft("valuart", v)} ro={!im.puo_modificare}
        onSaved={() => { setDrafts((p) => { const x = { ...p }; delete x.valuart; return x; }); load(); }}
        indirizzo={[im.indirizzo, im.numero_civico || im.civico].filter(Boolean).join(" ")} comune={im.comune} mq={im.mq} tipologia={im.tipologia}>
        <div className="card-immo p-5 space-y-3" data-testid="valuart-extra">
          {[["comparabili", "Comparabili (facoltativo)"], ["note", "Note"]].map(([k, l]) => (
            <label key={k} className="block"><span className="block text-[11px] font-semibold text-[var(--muted)] mb-1">{l}</span>
              <textarea data-testid={`valuart-${k}`} rows={3} disabled={!im.puo_modificare} value={(drafts.valut || {})[k] ?? im.valutazione?.[k] ?? ""}
                onChange={(e) => setDraft("valut", { comparabili: im.valutazione?.comparabili ?? "", note: im.valutazione?.note ?? "", ...(drafts.valut || {}), [k]: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm disabled:bg-slate-50" /></label>))}
          {im.puo_modificare && <PrezzoPubblicazione id={id} consigliato={im.valuart_ultima?.prezzo || im.valutazione?.prezzo_consigliato} prezzo={{ approvato: im.prezzo_approvato, da_approvare: im.prezzo_da_approvare, prezzo: im.prezzo }} onChange={load} />}
        </div>
      </ValuArt></div>}
      {(dubbio || decidi) && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={dubbio ? "dubbio-modal" : "decidi-modal"}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setDubbio(null); setDecidi(null); }} />
          <div className="relative w-full max-w-md card-immo p-6">
            <h3 className="font-display text-lg font-bold mb-1">{dubbio ? "Documento dubbio" : "Decidi sul documento"}</h3>
            <p className="text-sm text-[var(--muted)] mb-3">{DOC_LABELS[dubbio || decidi]} — {dubbio ? "il direttore del team riceverà la richiesta." : `nota della segreteria: ${im.documenti_verifica?.[decidi]?.nota || "—"}`}</p>
            <LongTextarea data-testid="dubbio-nota" value={notaD} onChange={(e) => setNotaD(e.target.value)} rows={3} autoFocus
                      placeholder={dubbio ? "Cosa ti lascia il dubbio? (obbligatoria)" : "Nota (obbligatoria se rimandi all'agente)"}
                      className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] outline-none text-sm mb-4" />
            {dubbio ? (
              <div className="flex gap-2">
                <button onClick={() => setDubbio(null)} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
                <button data-testid="dubbio-submit" onClick={submitDubbio} className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white font-semibold text-sm">Invia al direttore</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button data-testid="decidi-rimanda" onClick={() => submitDecidi("rimanda")} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Rimanda all'agente</button>
                <button data-testid="decidi-approva" onClick={() => submitDecidi("approva")} className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white font-semibold text-sm">Approva</button>
              </div>
            )}
          </div>
        </div>
      )}
      {scontoModal && <ProponiScontoModal immobileId={id} titolo={im.titolo} onClose={() => setScontoModal(false)} />}
      {rapporto && <RapportoModal id={id} onClose={() => setRapporto(false)} />}
      {showDelete && <DeleteEntityDialog entityType="immobile" pathBase="/immobili" id={id} name={im.titolo}
                                         onClose={() => setShowDelete(false)} onDeleted={() => nav("/immobili")} />}

      {rejectDoc && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setRejectDoc(null)} />
          <div className="relative w-full max-w-md card-immo p-6">
            <h3 className="font-display text-lg font-bold mb-1">Rifiuta documento</h3>
            <p className="text-sm text-[var(--muted)] mb-4">{DOC_LABELS[rejectDoc]} — indica all'agente cosa correggere.</p>
            <LongTextarea data-testid="reject-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} autoFocus
                      placeholder="Motivazione del rifiuto (obbligatoria)"
                      className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-red-400 outline-none text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setRejectDoc(null)} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
              <button data-testid="reject-submit" onClick={submitReject}
                      className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-sm">Rifiuta documento</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Bozza.Provider>
  );
}

const Kv = ({ k, v }) => <div className="flex justify-between"><span className="text-[var(--muted)]">{k}</span><span className="font-medium">{v || "-"}</span></div>;

function RegolaOroCard({ im, id, onSaved }) {
  const [pv, setPv] = useState(!!im.prov_venditore_incassata);
  const [pa, setPa] = useState(!!im.prov_acquirente_incassata);
  const [busy, setBusy] = useState(false);
  const [avviso, setAvviso] = useState(false);
  const [prevista, setPrevista] = useState(im.data_prevista_incasso || "");
  const { isDirezione } = useAuth();
  const [inc, setInc] = useState({});
  const [sbloccato, setSbloccato] = useState(false);
  useEffect(() => {
    api.get(`/immobili/${id}/provvigioni`).then((r) => setInc(Object.fromEntries(r.data.incassi.filter((x) => x.provvigione_id && x.stato === "incassata").map((x) => [x.lato, true])))).catch(() => setInc({}));
  }, [id, im.updated_at]);
  const lockV = inc.venditore && !sbloccato, lockA = inc.acquirente && !sbloccato;
  const salva = async (conferma) => {
    if (!(pv && pa) && !conferma) { setAvviso(true); return; }
    if (!(pv && pa) && !prevista) return toast.error("Indica la data prevista di incasso");
    setBusy(true);
    try {
      const r = await api.post(`/immobili/${id}/preliminare/firma`, { firmato: true, provvigione_venditore_incassata: pv, provvigione_acquirente_incassata: pa, data_prevista_incasso: prevista });
      toast[r.data?.pratica_bloccata ? "warning" : "success"](r.data?.pratica_bloccata ? "Preliminare registrato — pratica in rosso finché la provvigione non è incassata (Soci avvisati)" : "Preliminare registrato: pratica regolare");
      setAvviso(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-immo p-5 mt-4" data-testid="regola-oro-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1"><FileSignature size={14} /> Preliminare — Regola d'oro</div>
      <p className="text-sm text-[var(--muted)] mb-3">La provvigione si incassa al preliminare. Se firmato senza incasso, la pratica resta in rosso e vengono avvisati i Soci.</p>
      {im.pratica_bloccata && <div data-testid="regola-oro-rosso" className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">Pratica in rosso: provvigione non incassata{im.data_prevista_incasso ? ` · incasso previsto ${fmtDate(im.data_prevista_incasso)}` : ""}</div>}
      <label className={`flex items-center gap-2 text-sm mb-2 ${lockV ? "opacity-70" : ""}`}><input data-testid="prov-venditore" type="checkbox" checked={pv} disabled={lockV} onChange={(e) => setPv(e.target.checked)} /> Provvigione venditore incassata{lockV && <Lock size={12} className="text-slate-500" />}</label>
      <label className={`flex items-center gap-2 text-sm mb-3 ${lockA ? "opacity-70" : ""}`}><input data-testid="prov-acquirente" type="checkbox" checked={pa} disabled={lockA} onChange={(e) => setPa(e.target.checked)} /> Provvigione acquirente incassata{lockA && <Lock size={12} className="text-slate-500" />}</label>
      {(inc.venditore || inc.acquirente) && (
        <div data-testid="regola-oro-lock" className="text-[11px] text-[var(--muted)] mb-3 flex items-center gap-2 flex-wrap">
          {sbloccato ? "Caselle sbloccate (Soci)" : "Provvigione già incassata: casella bloccata, la possono sbloccare solo i Soci."}
          {isDirezione && !sbloccato && <button data-testid="regola-oro-sblocca" type="button" onClick={() => setSbloccato(true)} className="px-2 py-0.5 rounded-md border border-[var(--border)] bg-white font-semibold text-slate-700">Sblocca</button>}
        </div>
      )}
      <button data-testid="preliminare-registra" onClick={() => salva(false)} disabled={busy} className="px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-50">{busy ? "Salvo..." : "Registra firma preliminare"}</button>
      {avviso && (
        <div data-testid="regola-oro-avviso" className="mt-3 p-3 rounded-xl border-2 border-[#FFB020]/50 bg-[#FFB020]/10">
          <div className="text-sm font-semibold text-[#9A6A00] mb-2">Attenzione: provvigione non ancora incassata. Data prevista incasso?</div>
          <input type="date" data-testid="regola-oro-data" value={prevista} onChange={(e) => setPrevista(e.target.value)} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm bg-white" />
          <div className="flex gap-2 mt-3">
            <button data-testid="regola-oro-annulla" onClick={() => setAvviso(false)} className="flex-1 py-2 rounded-lg bg-white border border-[var(--border)] text-sm font-medium">Annulla</button>
            <button data-testid="regola-oro-conferma" onClick={() => salva(true)} disabled={busy || !prevista} className="flex-1 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold disabled:opacity-40">Conferma e salva</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RapportoModal({ id, onClose }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/immobili/${id}/rapporto-proprietario/anteprima`).then((r) => setA(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); }); }, [id]); // eslint-disable-line
  const invia = async () => {
    setBusy(true);
    try { const r = await api.post(`/immobili/${id}/rapporto-proprietario/invia`); if (r.data.bloccato) toast.warning(`Bloccato (anteprima): nessuna email a ${r.data.to}`); else toast.success(`Rapporto inviato a ${r.data.to}`); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const stop = a && (a.bloccato || a.gia_inviato_oggi);
  return (
    <Modal title="Rapporto al proprietario — anteprima" onClose={onClose} testid="rapporto-modal" saveLabel={busy ? "Invio…" : "Invia ora"} saving={busy || !a || !!stop} onSave={invia}>
      {!a ? <div className="skeleton h-32 rounded-xl" /> : (
        <div className="space-y-3">
          <div className="text-sm"><span className="text-[var(--muted)]">A:</span> <b data-testid="rapporto-to">{a.to || "—"}</b></div>
          <pre data-testid="rapporto-corpo" className="whitespace-pre-wrap text-sm bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-3 font-sans">{a.corpo}</pre>
          {a.bloccato && <div data-testid="rapporto-bloccato" className="text-sm text-red-600">{a.bloccato}</div>}
          {a.gia_inviato_oggi && <div data-testid="rapporto-gia-inviato" className="text-sm text-[#9A6A00]">Rapporto già inviato oggi per questo immobile.</div>}
        </div>
      )}
    </Modal>
  );
}

function ProponiScontoModal({ immobileId, titolo, onClose }) {
  const [pct, setPct] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [busy, setBusy] = useState(false);
  const P0 = { tipo: "pct", valore: "" };
  const [sc, setSc] = useState({ std_venditore: P0, rich_venditore: P0, std_acquirente: P0, rich_acquirente: P0, importo_sconto: "", motivo: "" });
  useEffect(() => {
    api.get(`/immobili/${immobileId}`).then(({ data }) => {
      const st = (l) => (data[`provv_${l}_modo`] === "eur" ? { tipo: "forfait", valore: String(data[`provv_${l}_eur`] ?? "") } : { tipo: "pct", valore: String(data[`provv_${l}_pct`] ?? "") });
      setSc((s) => ({ ...s, std_venditore: st("venditore"), std_acquirente: st("acquirente") }));
    }).catch(() => {});
  }, [immobileId]);
  const okSc = parseFloat(String(pct).replace(",", ".")) > 0 && ["std_venditore", "rich_venditore", "std_acquirente", "rich_acquirente"].every((k) => sc[k].valore !== "") && parseFloat(sc.importo_sconto) > 0 && sc.motivo && descrizione.trim().length >= 150;
  const submit = async () => {
    const p = parseFloat(String(pct).replace(",", "."));
    if (!p || p <= 0) return toast.error("Indica una percentuale di sconto valida.");
    setBusy(true);
    try {
      const r = await api.post("/sconti/richiedi", { immobile_id: immobileId, sconto_pct: p, descrizione: sc.motivo, motivazione: descrizione, ...sc });
      toast.success(r.data?.livello === "soci" ? "Sconto oltre soglia: inviato ai Soci per approvazione." : "Sconto inviato al Direttore per approvazione.");
      onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="proponi-sconto-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-immo p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2"><BadgePercent size={18} className="text-[#9A6A00]" /> Proponi sconto provvigione</h3>
        <p className="text-sm text-[var(--muted)] mb-4">{titolo} — entro soglia approva il Direttore, oltre soglia i Soci.</p>
        <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Sconto (%)</label>
        <input data-testid="sconto-pct-input" type="number" min="0" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} autoFocus
               placeholder="Es. 0.5" className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm mb-4" />
        <div className="grid grid-cols-2 gap-2 mb-2" data-testid="sconto-provv">
          <Prov label="Venditore – standard *" value={sc.std_venditore} onChange={(v) => setSc({ ...sc, std_venditore: v })} testid="sconto-std-vend" />
          <Prov label="Venditore – richiesta *" value={sc.rich_venditore} onChange={(v) => setSc({ ...sc, rich_venditore: v })} testid="sconto-rich-vend" />
          <Prov label="Acquirente – standard *" value={sc.std_acquirente} onChange={(v) => setSc({ ...sc, std_acquirente: v })} testid="sconto-std-acq" />
          <Prov label="Acquirente – richiesta *" value={sc.rich_acquirente} onChange={(v) => setSc({ ...sc, rich_acquirente: v })} testid="sconto-rich-acq" />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <label className="text-[11px] font-semibold text-[var(--muted)]">Importo sconto (€) *<input data-testid="sconto-importo" type="number" min="0" value={sc.importo_sconto} onChange={(e) => setSc({ ...sc, importo_sconto: e.target.value })} className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm mt-1" /></label>
          <label className="text-[11px] font-semibold text-[var(--muted)]">Motivo *<select data-testid="sconto-motivo" value={sc.motivo} onChange={(e) => setSc({ ...sc, motivo: e.target.value })} className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm mt-1"><option value="">—</option>{MOTIVI_SCONTO.map((m) => <option key={m}>{m}</option>)}</select></label>
        </div>
        <Motivazione value={descrizione} onChange={setDescrizione} testid="sconto-desc-input" />
        <div className="h-4" />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium hover:bg-slate-100">Annulla</button>
          <button data-testid="sconto-submit-btn" onClick={submit} disabled={busy || !okSc}
                  className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] hover:opacity-90 text-black font-semibold text-sm disabled:opacity-50">{busy ? "Invio..." : "Invia richiesta"}</button>
        </div>
      </div>
    </div>
  );
}

const TAPPE = [["valutazione", "Valutazione"], ["presa_in_carico", "Presa in carico"], ["raccolta_documentazione", "Raccolta documentazione"], ["strategia_mirata", "Strategia mirata"], ["open_house", "Open house"], ["raccolta_offerte", "Raccolta offerte"], ["accettazione", "Accettazione"], ["preliminare", "Preliminare"], ["rogito", "Rogito"]];
function TappeBarra({ tappa, stato }) {
  const cur = Math.max(0, TAPPE.findIndex(([k]) => k === tappa));
  return (
    <div className="card-immo p-4 mb-6 overflow-x-auto" data-testid="tappe-barra">
      <div className="flex items-center gap-1 min-w-max">
        {TAPPE.map(([k, label], i) => {
          const done = i < cur, now = i === cur;
          return (
            <div key={k} className="flex items-center gap-1" data-testid={`tappa-${k}`}>
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${now ? "bg-[#FC82ED] text-black border-transparent" : done ? "bg-emerald-400/10 text-emerald-600 border-emerald-400/30" : "bg-slate-100 text-slate-400 border-slate-200"}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${now ? "bg-black/20" : done ? "bg-emerald-500 text-white" : "bg-slate-300 text-white"}`}>{done ? "✓" : i + 1}</span>
                {label}
              </div>
              {i < TAPPE.length - 1 && <span className="text-slate-300 text-xs">→</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CensitoPanel({ im, id, onDone }) {
  const [modal, setModal] = useState(false);
  const cat = (Array.isArray(im.catasto_unita) && im.catasto_unita[0]) || im.catasto || {};
  return (
    <div className="space-y-6" data-testid="immobile-censito-panel">
      <div className="card-immo p-5 border-[#FFB020]/30 bg-[#FFB020]/[0.06] flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold text-[#FFB020] uppercase text-sm">Immobile censito – da trasformare in incarico</div>
          <p className="text-sm text-slate-600 mt-1">Scheda anagrafica preliminare con foto. La scheda tecnica completa (superficie e vani, camere e bagni, piano, prezzo, documenti, checklist, portali e dati incarico) sarà disponibile dopo la trasformazione in incarico. Le foto caricate ora passeranno automaticamente all'incarico.</p>
        </div>
        <button data-testid="trasforma-incarico-btn" onClick={() => setModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-all">
          <Rocket size={16}/> Trasforma in incarico
        </button>
      </div>
      {im.puo_modificare && <DatiBaseEdit im={im} Bozza={Bozza} soci={false} completo={false} />}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-immo p-5 space-y-2 text-sm">
          <div className="font-semibold mb-1 flex items-center gap-2"><Building2 size={16}/> Dati immobile</div>
          <Kv k="Indirizzo" v={im.indirizzo} />
          <Kv k="Numero civico" v={im.numero_civico} />
          <Kv k="Tipologia" v={tipoCatasto(im.tipologia, im.categoria_catastale)} />
          <Kv k="Zona" v={im.zona} />
          <Kv k="Comune" v={im.comune} />
          <Kv k="Foglio" v={cat.foglio || im.foglio} />
          <Kv k="Particella" v={cat.particella || im.particella} />
          <Kv k="Subalterno" v={cat.subalterno || im.subalterno} />
        </div>
        <div className="card-immo p-5 space-y-2 text-sm">
          <div className="font-semibold mb-1 flex items-center gap-2"><Users size={16}/> Proprietario / Venditore</div>
          {im._masked ? <div className="text-[var(--muted)] flex items-center gap-2"><Lock size={14}/> Dati riservati · Agente {im.owner_agent_name}</div> : <>
            <div className="font-medium">{im.proprietario_nome} {im.proprietario_cognome}</div>
            {im.telefono && <div className="flex items-center gap-2 text-slate-500"><Phone size={13}/>{im.telefono}</div>}
            {im.email && <div className="flex items-center gap-2 text-slate-500"><Mail size={13}/>{im.email}</div>}
            {im.proprietario_id && <a data-testid="censito-cliente-link" href={`/proprietari?open=${im.proprietario_id}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#FC82ED] hover:underline"><Users size={13}/> Apri scheda Cliente Venditore</a>}
            <ProprietariEdit im={im} Bozza={Bozza} />
          </>}
        </div>
      </div>
      <div className="card-immo p-5">
        <PhotoManager entityType="immobile" entityId={id} readOnly />
      </div>
      {modal && <IncaricoModal im={im} id={id} onClose={() => setModal(false)} onSaved={() => { setModal(false); onDone(); }} />}
    </div>
  );
}

function IncaricoModal({ im, id, edit, onClose, onSaved }) {
  const [f, setF] = useState({ tipo_incarico: im.tipo_incarico || "", percentuale_provvigione: im.percentuale_provvigione ?? "" });
  const [saving, setSaving] = useState(false);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const n = Number(f.percentuale_provvigione);
  const punti = f.tipo_incarico === "esclusiva" ? (n === 3 ? 2 : n === 4 ? 4 : 0) : 0;
  const save = async () => {
    const errs = validate({ tipo_incarico: [V.required], percentuale_provvigione: [V.required, V.nonNegative] }, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    setSaving(true);
    const body = { tipo_incarico: f.tipo_incarico, percentuale_provvigione: Number(f.percentuale_provvigione) };
    try {
      const r = edit ? await api.put(`/immobili/${id}`, body) : await api.post(`/immobili/${id}/trasforma-incarico`, body);
      if (edit && r.data?.provvigione_in_attesa) toast.warning("% sotto lo standard: in attesa di approvazione in \"Da approvare → Sconti provvigione\".");
      else toast.success(edit ? "Dati incarico aggiornati" : "Immobile trasformato in incarico");
      onSaved();
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={edit ? "Dati incarico" : "Trasforma in incarico"} testid="incarico-modal" onClose={onClose} onSave={save} saving={saving} saveLabel={edit ? "Salva" : "Trasforma in incarico"} summary={summary} scheda value={f} onRestore={setF}>
      <Select label="Tipo di incarico" required testid="incarico-tipo" error={errors.tipo_incarico} value={f.tipo_incarico} onChange={(e) => { setF({ ...f, tipo_incarico: e.target.value }); clear("tipo_incarico"); }} opts={[["", "— Seleziona —"], ["esclusiva", "Esclusiva"], ["non_esclusiva", "Non esclusiva"]]} />
      <Field label="% provvigione incarico" required testid="incarico-percentuale" type="number" error={errors.percentuale_provvigione} value={f.percentuale_provvigione} onChange={(e) => { setF({ ...f, percentuale_provvigione: e.target.value }); clear("percentuale_provvigione"); }} />
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm flex items-center justify-between">
        <span className="text-slate-600">Punti contest incarico</span>
        <span data-testid="incarico-punti-preview" className="inline-flex items-center gap-1 font-bold text-amber-600"><Award size={14} /> {punti} punti</span>
      </div>
      <p className="text-xs text-[var(--muted)]">Solo gli incarichi <b>in esclusiva</b> al 3% (2 pt) o 4% (4 pt) assegnano punti. Agente titolare: {im.owner_agent_name || "—"}.</p>
    </Modal>
  );
}
function ListSimple({ items = [], render, empty, icon: Icon }) {
  if (!items.length) return <div className="card-immo p-8 text-center text-sm text-[var(--muted)]">{empty}</div>;
  return <div className="space-y-2">{items.map((it, i) => <div key={it.id || i} className="card-immo p-4 flex items-center gap-3 text-sm"><Icon size={16} className="text-[#FC82ED]"/>{render(it)}</div>)}</div>;
}
